'use client';
import React from 'react';
import { SplitImageHeroProps } from '@/components/hero/types';
import { BaseImage } from '@/components/baseImage/BaseImage';
import { Link } from '@/components/link/Link';
import Card from '@/components/card/Card';
import clsx from 'clsx';
import { Headline } from '@/components/headline/Headline';
import SerializeDescription from '@/components/serialize-description/SerializeDescription';

const SplitImageHero: React.FC<SplitImageHeroProps> = ({
  headline,
  description,
  link,
  card,
  image,
  sectionBackgroundColor,
  spacingTop,
  spacingBottom,
  sectionId,
}) => {
  return (
    <>
      <div
        id={sectionId}
        className={`block ${
          spacingTop && spacingBottom
            ? 'py-4 md:my-8 lg:py-20'
            : spacingTop
              ? 'pt-4 md:pt-8 lg:pt-20'
              : spacingBottom
                ? 'pb-4 md:pb-8 lg:pb-20'
                : ''
        }`}
      >
        <div
          className={`before:bg-accent after:bg-accent relative w-full before:absolute before:bottom-0 before:left-[-3vw] before:right-[80%] before:top-0 before:z-0 before:w-full before:max-w-[27.8vw] after:absolute after:bottom-0 after:left-[78%] after:right-0 after:top-0 after:w-full after:max-w-[21.8vw] lg:after:bg-${sectionBackgroundColor} after:z-0`}
        >
          <div
            className={
              'before:bg-accent after:bg-accent relative hidden w-full before:absolute before:bottom-0 before:left-[-3vw] before:right-[80%] before:top-0 before:z-0 before:w-full before:max-w-[21vw] after:absolute after:bottom-0 after:left-[80%] after:right-0 after:top-0 after:z-0 after:w-full after:max-w-[21.1vw] lg:after:bg-light-blue'
            }
          ></div>
          <div className="split-image-hero-container container relative mx-auto pb-6 sm:px-0 md:grid md:grid-cols-4 md:gap-4 md:px-4 lg:grid-cols-12 lg:px-0 lg:pb-0 xl:grid-cols-12">
            <div
              className={clsx(
                'split-image-hero__content relative z-10 col-start-1 col-end-5 flex flex-col justify-center',
                card ? 'justify-end' : 'justify-start',
                'lg:col-start-1 lg:col-end-7 xl:col-span-7 xl:col-start-1 xl:col-end-7',
              )}
            >
              <div className="p-4 sm:py-12">
                <Headline
                  {...headline}
                  className="split-image-hero__title mb-4 font-bold text-brand-700 lg:mb-6"
                >
                  {headline.title}
                </Headline>
                {description && (
                  <div className="split-image-hero__description text-muted-foreground mb-6 text-lg">
                    <SerializeDescription description={description} />
                  </div>
                )}
                {link && (
                  <div className="split-image-hero__link hidden w-fit lg:mb-4 lg:block">
                    {link && <Link {...link} className="px-0" />}
                  </div>
                )}
              </div>

              {card && (
                <div className="split-image-hero__news-card bg-background hidden lg:block lg:w-9/12 xl:w-1/2">
                  <p className="my-2 text-xs font-medium uppercase leading-6 text-blue">
                    Aktuell
                  </p>
                  <Card {...card} />
                </div>
              )}
            </div>

            {image ? (
              <div
                className={`split-image-hero__image hidden lg:grid bg-${sectionBackgroundColor} relative z-10 col-span-4 min-h-[700px] p-6 md:p-6 lg:col-start-7 lg:col-end-13 lg:p-20 lg:pr-0 xl:col-start-8 xl:col-end-13`}
              >
                <BaseImage
                  {...image}
                  className="mx-auto h-[540px] w-[440px] overflow-hidden !rounded-lg object-cover"
                  classNameImage="h-full rounded-lg w-full object-cover max-h-[540px]"
                  prerender={true}
                  preloadMedia="(min-width: 1024px)"
                  sizes="440px"
                />
              </div>
            ) : null}

            {image ? (
              <div className={'col-span-4 block bg-light-blue lg:hidden'}>
                <div
                  className={`split-image-hero__image bg-${sectionBackgroundColor} relative z-10 aspect-[16/10] min-h-fit p-6 md:p-6 lg:col-start-7 lg:col-end-13 lg:p-20 lg:pr-0 xl:col-start-8 xl:col-end-13`}
                >
                  <BaseImage
                    {...image}
                    className="h-full w-full overflow-hidden rounded-lg"
                    classNameImage="h-full w-full object-cover aspect-[16/10]"
                    prerender={true}
                    preloadMedia="(max-width: 1023.98px)"
                    sizes="(max-width: 1024px) 100vw, 50vw"
                  />
                </div>
              </div>
            ) : null}

            {link && (
              <div className="split-image-hero__link ml-4 mt-4 block w-fit lg:mb-4 lg:hidden">
                {link && <Link {...link} className="px-0" />}
              </div>
            )}

            {card && (
              <div className="split-image-hero__news-card bg-background col-span-4 mx-4 my-8 lg:hidden">
                <Card {...card} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SplitImageHero;
